import React from "react";
import { styled } from "@mui/material/styles";
import { Stack, Grid, Accordion, GridProps, Typography, Box, useMediaQuery } from "@mui/material";
import { AccordionDetails, AccordionSummary, AccordionProps } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';

interface HeaderItemProps extends GridProps {
  first?: boolean
  title: string
  sortKey: string
  onChange: any
}

const ExpandMore = () => (
  <Stack direction="row" sx={{ width: '50px', justifyContent: 'center' }}>
    <ExpandMoreIcon fontSize="small" />
  </Stack>
)

const DefaultAccordion = (props: AccordionProps) => (
  <Accordion disableGutters elevation={0} square {...props} />
)

const TableWrapper = styled(Stack)(({ theme }) => ({
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.common.mainBg,

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0),
  },
}))

const TableHeader = ({ children }: any) => (
  <TableHeaderStyle>
    <TableGrid container>
      {children}
    </TableGrid>

    <Stack width={50} />
  </TableHeaderStyle>
)

const TableUnExpandHeader = ({ children }: any) => (
  <TableHeaderStyle>
    <TableGrid container>
      {children}
    </TableGrid>
  </TableHeaderStyle>
)

const TableHeaderItem = ({ onChange, sortKey, title, first }: HeaderItemProps) => {
  const isMd = useMediaQuery(`(max-width: 1280px)`);

  const sortAsc = `${title}ASC` === sortKey;
  const sortDesc = `${title}DESC` === sortKey;
  const sortType = sortAsc ? 'ASC' : sortDesc ? 'DESC' : '';

  const onClick = () => {
    switch (sortType) {
      case 'ASC':
        onChange('')
        break;

      case 'DESC':
        onChange(`${title}ASC`)
        break;

      default:
        onChange(`${title}DESC`)
        break;
    }
  }

  return (
    <TableHeaderItemStyle item xs={12} md={6} lg={4} xl={3} onClick={onClick}>
      {(first && !isMd) && (
        <Stack direction='row' gap={1}>
          <Stack width={20} />
          <Typography variant="body1">{title}</Typography>
        </Stack>
      )}

      {(!first || isMd) && (
        <Typography variant="body1">{title}</Typography>
      )}

      <Stack direction='row' gap={0.5}>
        {sortType === 'ASC' && (
          <NorthIcon sx={{ fontSize: '1rem' }} />
        )}

        {sortType === 'DESC' && (
          <SouthIcon sx={{ fontSize: '1rem' }} />
        )}

        <MoreVertIcon sx={{ fontSize: '1rem' }} />
      </Stack>
    </TableHeaderItemStyle>
  )
}

const TableBody = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}))

const TableRowContainer = styled(DefaultAccordion)(({ theme }) => ({
  width: '100%',
  margin: '0px',
  padding: '0px',
  borderRadius: '0px',
  background: 'unset',
  boxShadow: `0px 0px 5px ${theme.palette.common.firstBorder}`,

  '&:not(:last-child)': {
    margin: '0px',
  },

  '&:before': {
    display: 'none',
  },

  '.Mui-expanded': {
    minHeight: 'unset !important',
  }
}))

const TableRow = ({ children }: any) => (
  <TableRowStyle expandIcon={<ExpandMore />}>
    <TableGrid container>
      {children}
    </TableGrid>
  </TableRowStyle>
)

const TableUnExpandRow = ({ children }: any) => (
  <TableRowStyle>
    <TableGrid container>
      {children}
    </TableGrid>
  </TableRowStyle>
)

const TableRowItem = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',

  padding: theme.spacing(1, 3),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1, 2),
  },
}))

const TableRowDetails = styled(AccordionDetails)({
  display: 'flex',
  flexDirection: 'column',
})

// common component
const TableGrid = styled(Grid)({
  margin: '0px',
  padding: '0px',
})

const TableHeaderStyle = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  alignItems: "center",
  backgroundColor: theme.palette.common.mainBg,
  boxShadow: `0px 0px 5px ${theme.palette.common.firstBorder}`
}))

const TableHeaderItemStyle = styled(Grid)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  cursor: 'pointer',
  userSelect: 'none',
  padding: theme.spacing(2, 3),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1, 2),
  },
}))

const TableRowStyle = styled(AccordionSummary)(({ theme }) => ({
  width: '100%', margin: '0px', padding: '0px',
  backgroundColor: theme.palette.common.mainBg,

  '& .MuiAccordionSummary-content': {
    margin: '0px !important', padding: '0px',
  }
}))

export {
  TableWrapper, TableHeader, TableUnExpandHeader, TableHeaderItem, TableRowDetails,
  TableBody, TableRowContainer, TableRow, TableUnExpandRow, TableRowItem
}